import { keepPreviousData, useQuery } from '@tanstack/react-query';

import { apiClient } from '@/api/client';
import { mapFamilySummaries } from '@/features/families/mappers';
import type { FamilySummaryViewModel } from '@/models/family';

import { useDebouncedValue } from './useDebouncedValue';
import {
  PATIENT_SEARCH_DEFAULT_LIMIT,
  PATIENT_SEARCH_MIN_LENGTH,
  type UsePatientSearchOptions,
} from './usePatientSearch';

const DEBOUNCE_MS = 200;

export interface UseFamilySearchResult {
  readonly results: readonly FamilySummaryViewModel[];
  readonly isLoading: boolean;
  readonly error: Error | null;
}

/**
 * Server-side quick-search over `GET /families/search?name=` (PTV2-1232 #5).
 * Unlike persons, families expose a real name search, so this is genuinely
 * search-as-you-type. Debounced + min-length gated, mirroring `usePersonSearch`
 * so the QuickSearch palette can render a Families section alongside Patients.
 */
export function useFamilySearch(
  rawQuery: string,
  options: UsePatientSearchOptions = {},
): UseFamilySearchResult {
  const limit = options.limit ?? PATIENT_SEARCH_DEFAULT_LIMIT;
  const debounceMs = options.debounceMs ?? DEBOUNCE_MS;

  const trimmed = rawQuery.trim();
  const debouncedQuery = useDebouncedValue(trimmed, debounceMs);
  const isActive = debouncedQuery.length >= PATIENT_SEARCH_MIN_LENGTH;

  const query = useQuery<readonly FamilySummaryViewModel[]>({
    queryKey: ['families', 'search', debouncedQuery, limit] as const,
    queryFn: async ({ signal }) => {
      const rows = await apiClient.searchFamilies(
        { name: debouncedQuery, size: limit },
        { signal },
      );
      return mapFamilySummaries(rows);
    },
    enabled: isActive,
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });

  const debounceSettling = trimmed !== debouncedQuery;
  const isLoading = isActive && (debounceSettling || query.isFetching);

  return {
    results: isActive ? (query.data ?? []) : [],
    isLoading,
    error: isActive ? (query.error ?? null) : null,
  };
}
